import { z } from "zod";
import { BOARD_SIZE, MAX_SELFIE_BASE64_BYTES, TIMER_OPTIONS } from "./config";

export const nicknameSchema = z
  .string()
  .trim()
  .min(1, "Enter a nickname")
  .max(24)
  .refine(
    (value) =>
      ![...value].some((character) => {
        const codePoint = character.codePointAt(0) ?? 0;
        return character === "<" || character === ">" || codePoint <= 0x1f || codePoint === 0x7f;
      }),
    "Nickname contains unsafe characters"
  );

export const gameCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[0-9A-HJKMNP-TV-Z]{10}$/);

export const timerSecondsSchema = z.union([
  z.null(),
  z.literal(TIMER_OPTIONS[0]),
  z.literal(TIMER_OPTIONS[1]),
  z.literal(TIMER_OPTIONS[2]),
  z.literal(TIMER_OPTIONS[3]),
  z.literal(TIMER_OPTIONS[4]),
  z.literal(TIMER_OPTIONS[5]),
  z.literal(TIMER_OPTIONS[6])
]);

export const createGameSchema = z.object({
  nickname: nicknameSchema,
  timerSeconds: timerSecondsSchema.default(null)
});
export const joinGameSchema = z.object({ nickname: nicknameSchema });
export const actionBaseSchema = z.object({
  revision: z.number().int().nonnegative(),
  idempotencyKey: z.string().uuid()
});
export const placementSchema = z.object({
  tileId: z.string().min(1).max(32),
  row: z
    .number()
    .int()
    .min(0)
    .max(BOARD_SIZE - 1),
  col: z
    .number()
    .int()
    .min(0)
    .max(BOARD_SIZE - 1),
  blankLetter: z
    .string()
    .regex(/^[A-Z]$/)
    .optional()
});
export const placeMoveSchema = actionBaseSchema.extend({
  placements: z.array(placementSchema).min(1).max(7)
});
export const exchangeMoveSchema = actionBaseSchema.extend({
  tileIds: z
    .array(z.string().min(1).max(32))
    .min(1)
    .max(7)
    .refine((ids) => new Set(ids).size === ids.length)
});
export const scorelessActionSchema = actionBaseSchema;
export const rematchSchema = z.object({ idempotencyKey: z.string().uuid() });

const notificationEndpointSchema = z
  .string()
  .trim()
  .min(1)
  .max(4096)
  .refine((value) => {
    try {
      const endpoint = new URL(value);
      return (
        endpoint.protocol === "https:" &&
        endpoint.username === "" &&
        endpoint.password === "" &&
        endpoint.hash === ""
      );
    } catch {
      return false;
    }
  }, "Push endpoint must be a valid HTTPS URL without credentials or a fragment");

export const notificationSubscriptionSchema = z
  .object({
    endpoint: notificationEndpointSchema,
    expirationTime: z.number().int().nonnegative().nullable().optional().default(null),
    keys: z
      .object({
        p256dh: z.string().regex(/^[A-Za-z0-9_-]{87}$/u),
        auth: z.string().regex(/^[A-Za-z0-9_-]{22}$/u)
      })
      .strict()
  })
  .strict();

export const notificationUnsubscribeSchema = z
  .object({ endpoint: notificationEndpointSchema })
  .strict();

export const selfieSchema = z.object({
  mimeType: z.enum(["image/jpeg", "image/png", "image/webp"]),
  base64: z
    .string()
    .min(16)
    .max(Math.ceil(MAX_SELFIE_BASE64_BYTES * 1.4)),
  width: z.number().int().positive().max(960),
  height: z.number().int().positive().max(960)
});

export type CreateGameInput = z.infer<typeof createGameSchema>;
export type JoinGameInput = z.infer<typeof joinGameSchema>;
export type PlaceMoveInput = z.infer<typeof placeMoveSchema>;
export type ExchangeMoveInput = z.infer<typeof exchangeMoveSchema>;
export type ScorelessActionInput = z.infer<typeof scorelessActionSchema>;
export type NotificationSubscriptionInput = z.infer<typeof notificationSubscriptionSchema>;
export type NotificationUnsubscribeInput = z.infer<typeof notificationUnsubscribeSchema>;
export type SelfieInput = z.infer<typeof selfieSchema>;
